import { create } from 'zustand';
import type { Project } from '@ponzinomics/types';

const STORAGE_KEY = 'ponzinomics_current_project';

interface ProjectState {
  currentProject: Project | null;
  projects: Project[];
}

interface ProjectActions {
  setCurrentProject: (project: Project | null) => void;
  setProjects: (projects: Project[]) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  removeProject: (id: string) => void;
}

type ProjectStore = ProjectState & ProjectActions;

function resolveCurrentProject(
  projects: Project[],
  currentProject: Project | null
): Project | null {
  if (projects.length === 0) return null;

  const storedProjectId = localStorage.getItem(STORAGE_KEY);

  if (currentProject) {
    const matchingCurrentProject = projects.find((project) => project.id === currentProject.id);
    if (matchingCurrentProject) return matchingCurrentProject;
  }

  if (storedProjectId) {
    const storedProject = projects.find((project) => project.id === storedProjectId);
    if (storedProject) return storedProject;
  }

  return projects[0] ?? null;
}

export const useProjectStore = create<ProjectStore>((set) => ({
  // Initial state
  currentProject: null,
  projects: [],

  // Actions
  setCurrentProject: (project) => {
    set({ currentProject: project });
    // Persist selection
    if (project) {
      localStorage.setItem(STORAGE_KEY, project.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  },

  setProjects: (projects) => {
    set((state) => {
      const currentProject = resolveCurrentProject(projects, state.currentProject);

      if (currentProject) {
        localStorage.setItem(STORAGE_KEY, currentProject.id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }

      return { projects, currentProject };
    });
  },

  updateProject: (id, updates) => {
    set((state) => ({
      projects: state.projects.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      currentProject:
        state.currentProject?.id === id
          ? { ...state.currentProject, ...updates }
          : state.currentProject,
    }));
  },

  removeProject: (id) => {
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== id),
      currentProject: state.currentProject?.id === id ? null : state.currentProject,
    }));
  },
}));
